import { Autocomplete } from '@mui/material';
import type { TableFilterFn } from '@shared/ui/Table';
import { useMemo } from 'react';
import { LegacyInput } from '@/component/Form/Input';
import { useDataProvider } from '@/component/Table/DataProviderContext';
import { getInputProps } from '@/component/Table/filter/util';
import type { ColumnFilterProps } from '@/component/Table/types';
import { useTexts } from '@/feature/lang';
import type { ReportRow } from '../types';
import { EquipmentFilterPopper } from './EquipmentFilterPopper';

export type EquipmentOption = { id: string; name: string };

export const equipmentFilterFn: TableFilterFn<ReportRow> = (
  row,
  _columnId,
  selected: EquipmentOption | undefined
) =>
  !selected ||
  row.original.equipment.some(({ equipmentId }) => equipmentId === selected.id);

equipmentFilterFn.autoRemove = (value) => !value;

export const EquipmentFilter = ({ column, layout }: ColumnFilterProps) => {
  const texts = useTexts();
  const options = useEquipmentOptions();
  const selected =
    (column.getFilterValue() as EquipmentOption | undefined) ?? null;

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionKey={(option) => option.id}
      getOptionLabel={(option) => option.name}
      onChange={(_, value) => column.setFilterValue(value ?? undefined)}
      renderInput={(params) => (
        <LegacyInput {...params} {...getInputProps(column, layout, texts)} />
      )}
      slots={{ popper: EquipmentFilterPopper }}
      size="small"
    />
  );
};

function useEquipmentOptions(): EquipmentOption[] {
  const { table } = useDataProvider<ReportRow>();
  const rows = table?.getPreFilteredRowModel().rows;

  return useMemo(() => {
    const equipmentById = new Map<string, EquipmentOption>();
    rows?.forEach(({ original }) => {
      original.equipment.forEach(({ equipmentId, name }) => {
        equipmentById.set(equipmentId, { id: equipmentId, name });
      });
    });
    return [...equipmentById.values()].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [rows]);
}
